import React from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useDispatch, useSelector } from 'react-redux';
import { cn } from '@/utils/cn';
import { getImageUrl } from '@/utils/string';
import {
  resetProductFilters,
  updateFilters,
} from '@/redux/reducers/products-reducer';
import Text from '@/app/components/Text';
import { getProductBrandUrl, getProductCategoryUrl } from '@/url/product';
import { imageTypes } from '@/constants/types';
import { isCtrlClickTrigger } from '@/utils/object';
import ImageComponentV2 from '../Image/ImageComponentV2';
import TextSmall from '../Typography/TextSmall';

function Popover({
  showPopUpNavbar,
  secondaryNavItems,
  activePrimaryNav,
  handleMouseEnterSubmenu,
  handleMouseLeaveSubmenu,
  categories,
  onMenuClick = () => {},
  brands = null,
}) {
  const dispatch = useDispatch();
  const router = useRouter();
  const { lang } = useSelector((state) => state.globalSettings);

  const processString = (str) => str.replace(/\s+/g, '').toLowerCase();

  const handleNavigateToCategoryPage = async ({
    e,
    category = '',
    subCategory = '',
  }) => {
    if (subCategory) {
      e.stopPropagation();
    }

    // Build query params
    // const params = new URLSearchParams(searchParams);

    dispatch(resetProductFilters());

    // dispatch(setSelectedFilterItems(null));
    // dispatch(setSelectedColors([]));
    const params = new URLSearchParams();
    const slug = subCategory || category;

    params.set('category', slug);
    params.set('page', '1');
    params.set('limit', 12);

    // Update store filters
    dispatch(
      updateFilters({
        category: slug,
        page: 1,
        limit: 12,
      }),
    );

    // Update the url
    const url = `/category/${category}?${params.toString()}`;

    router.push(url);
  };

  const handleNavigateToBrandPage = async (e, brand) => {
    dispatch(resetProductFilters());

    router.push(getProductBrandUrl({ slug: brand.slug }));
  };

  const handleMenuClick = () => onMenuClick();

  // disable eslint rule for nested ternary
  /* eslint-disable no-nested-ternary */
  const renderMenu = () => {
    if (activePrimaryNav === 'installmentoptions') {
      return (
        <div
          className="w-full grid grid-cols-2 gap-[32px]"
          key="installmentoptions"
        >
          {secondaryNavItems?.map((menu) =>
            menu.submenu?.map(
              (item, index) =>
                menu.key === activePrimaryNav && (
                  <div
                    role="presentation"
                    key={item.key}
                    className="flex gap-2 items-center"
                    onClick={(e) => {
                      if (isCtrlClickTrigger(e)) {
                        return;
                      }

                      handleMenuClick();
                    }}
                  >
                    {item?.icon ? (
                      processString(item?.header) === 'ipdcezpay' ? (
                        <div className="max-w-[50px] max-h-[50px] overflow-hidden">
                          <ImageComponentV2
                            src={item?.icon}
                            sizes="100vw"
                            style={{
                              width: '100%',
                              height: 'auto',
                            }}
                            width={50}
                            height={50}
                            alt={`icon-${index + 1}`}
                            loading="lazy"
                          />
                        </div>
                      ) : (
                        <div className="max-w-[24px] max-h-[24px] overflow-hidden">
                          <ImageComponentV2
                            src={item?.icon}
                            sizes="100vw"
                            style={{
                              width: '100%',
                              height: 'auto',
                            }}
                            width={25}
                            height={25}
                            alt={`icon-${index + 1}`}
                            loading="lazy"
                          />
                        </div>
                      )
                    ) : (
                      <div className="w-[20px] h-[20px] rounded-8 bg-lightgrey" />
                    )}
                    <Text
                      type="paragraph"
                      className="text-16 text-coarse-wool hover:text-fire-chalk font-encode-sans font-bold leading-20 tracking-[0.32px] line-clamp-1"
                    >
                      <Link
                        prefetch={false}
                        aria-label="Go to installment option"
                        href={item.link}
                      >
                        {item.header}
                      </Link>
                    </Text>
                  </div>
                ),
            ),
          )}
        </div>
      );
    }

    if (activePrimaryNav === 'brands') {
      return (
        <div
          className="brands-options w-full grid grid-cols-12 gap-[50px] xl:gap-[60px]"
          key="brands"
        >
          <div className="col-span-4 min-w-[200px] flex flex-col items-center justify-center gap-[16px]">
            {brands?.top?.map((brand) => (
              <Link
                prefetch={false}
                aria-label="Go to top brand"
                key={`top-brand-${brand.slug}`}
                href={`${getProductBrandUrl({ slug: brand.slug })}`}
                passHref
                onClick={(e) => {
                  if (isCtrlClickTrigger(e)) {
                    return;
                  }

                  handleNavigateToBrandPage(e, brand);
                }}
              >
                <div
                  key={`top-brand-${brand.slug}`}
                  className="cursor-pointer "
                  role="presentation"
                  onClick={(e) => {
                    if (isCtrlClickTrigger(e)) {
                      return;
                    }

                    handleMouseLeaveSubmenu();
                  }}
                >
                  <ImageComponentV2
                    src={
                      brand?.navbar_icon
                        ? getImageUrl(brand?.navbar_icon)
                        : null
                    }
                    alt={brand?.name[lang]}
                    className="w-full max-w-[200px] h-[58px] object-contain object-center"
                    width={200}
                    height={58}
                    type={imageTypes.square}
                    loading="lazy"
                  />
                </div>
              </Link>
            ))}
          </div>

          {brands?.others.length > 0 ? (
            <div className="col-span-8 flex gap-[10px] lg:gap-[32px]">
              {secondaryNavItems?.map(
                (menu) =>
                  menu.key === activePrimaryNav &&
                  menu.submenu?.map(() => (
                    <div
                      key={menu.key}
                      className="flex flex-1 flex-col gap-[8px] 3xl:gap-[20px]"
                    >
                      <TextSmall
                        text="Others"
                        className="text-coarse-wool font-bold"
                      />

                      <div className="grid grid-cols-3 gap-x-0 md:gap-x-[20px] gap-y-[8px] 3xl:gap-y-[12px]">
                        {brands?.others?.map((brand) => (
                          <Link
                            prefetch={false}
                            aria-label="Go to other brand"
                            key={`other-brand-${brand.slug}`}
                            href={`${getProductBrandUrl({ slug: brand.slug })}`}
                            passHref
                            onClick={(e) => {
                              if (isCtrlClickTrigger(e)) {
                                return;
                              }

                              handleNavigateToBrandPage(e, brand);
                            }}
                          >
                            <div
                              key={`other-brand-${brand.slug}`}
                              className="text-center lg:text-left"
                              role="presentation"
                              onClick={(e) => {
                                if (isCtrlClickTrigger(e)) {
                                  return;
                                }

                                handleMenuClick();
                              }}
                            >
                              <TextSmall
                                text={brand?.name[lang]}
                                className="text-coarse-wool font-medium hover:text-fire-chalk line-clamp-1"
                              />
                            </div>
                          </Link>
                        ))}
                      </div>
                    </div>
                  )),
              )}
            </div>
          ) : null}
        </div>
      );
    }

    if (activePrimaryNav === 'categories') {
      return (
        <div
          className="categories-option w-full flex flex-col flex-wrap gap-x-[20px] h-full max-h-[512px] lg:max-h-[448px] overflow-x-auto thin-scroller"
          key="categories"
        >
          {categories?.map((category) => (
            <div
              role="presentation"
              onClick={(e) => {
                if (isCtrlClickTrigger(e)) {
                  return;
                }

                handleNavigateToCategoryPage({
                  e,
                  category: category.slug,
                });
                handleMouseLeaveSubmenu();
              }}
              key={category.id}
              className="parent-category flex h-auto pb-[12px]"
            >
              <div className="flex flex-col w-[277px] gap-y-[4px]">
                <div className="flex items-center gap-x-1">
                  <div className="max-w-[24px] max-h-[24px] overflow-hidden">
                    {category?.icon ? (
                      <ImageComponentV2
                        src={
                          category?.icon ? getImageUrl(category?.icon) : null
                        }
                        alt={category?.name[lang]}
                        className="max-w-[20px] w-full h-[20px] object-contain object-center"
                        width={20}
                        height={20}
                        loading="lazy"
                        type={imageTypes.icon}
                      />
                    ) : null}
                  </div>

                  <Link
                    prefetch={false}
                    aria-label="Go to product category"
                    passHref
                    href={getProductCategoryUrl({ slug: category.slug })}
                    className="line-clamp-1"
                  >
                    <TextSmall
                      text={category?.name[lang]}
                      className="text-coarse-wool hover:text-fire-chalk font-bold line-clamp-1"
                    />
                  </Link>
                </div>

                {category?.children?.length > 0 ? (
                  <div className="flex flex-col items-start gap-y-[4px]">
                    {category.children?.map((subCategory) => (
                      <div
                        role="presentation"
                        key={subCategory.id}
                        className="text-center lg:text-left"
                        onClick={(e) => {
                          if (isCtrlClickTrigger(e)) {
                            return;
                          }

                          handleNavigateToCategoryPage({
                            e,
                            category: category.slug,
                            subCategory: subCategory.slug,
                          });
                          handleMenuClick();
                        }}
                      >
                        <Link
                          prefetch={false}
                          aria-label="Go to subcategory"
                          passHref
                          href={getProductCategoryUrl({
                            slug: category.slug,
                            childrenSlug: subCategory.slug,
                          })}
                          className="line-clamp-1"
                        >
                          <TextSmall
                            text={subCategory?.name[lang]}
                            className=" text-coarse-wool hover:text-fire-chalk font-medium line-clamp-1 pl-[24px]"
                          />
                        </Link>
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      );
    }
  };

  return (
    <div
      className={cn(
        'popover absolute hidden lg:block z-[505] left-0 right-0 transition-all duration-100 ease-in-out transform',
        showPopUpNavbar
          ? 'opacity-100 translate-y-0 visible'
          : 'opacity-0 translate-y-1 pointer-events-none',
      )}
    >
      <div
        id="navigation-menu"
        className="popover bg-white lg:flex max-w-full lg:max-w-[1000px] 2xl:max-w-[1240px] mx-40 lg:mx-auto rounded-[12px] p-[20px] lg:px-[32px] lg:py-[24px] shadow-menu-expand"
        onMouseEnter={handleMouseEnterSubmenu}
        onMouseLeave={handleMouseLeaveSubmenu}
      >
        {renderMenu()}
      </div>
    </div>
  );
}

export default Popover;
